import { useState } from "react";
import { useHistory } from "react-router-dom";

import { isApiError } from "../../api/common";
import authApi from "../../api/public";
import twoFaApi from "../../api/twoFa";
import { useAuthErrorPopup, useSearchParams } from "../../common/hooks";
import { PNG_HEADER } from "../../constants/files";
import { useDispatch } from "../../redux/hooks";
import { setLoggedIn } from "../../redux/slices/auth/authSlice";
import { setCanRedirect } from "../../redux/slices/redirect/redirectSlice";
import { FormSubmissionHandler } from "../../types/common";
import { composeErrorHandlers, GenericError } from "../../utils/errorHandling";

import LoginForm, { LoginFormValues } from "./components/LoginForm";
import SetupOtpForm from "./components/SetupOtpForm";
import ValidateOtpForm from "./components/ValidateOtpForm";
import ForgotOTPForm from "./components/ForgotOTPForm";

enum LoginViewState {
    Password,
    SetupOtp,
    ValidateOtp,
    ForgotOtp,
}

const LoginView = (): JSX.Element => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [state, setState] = useState<LoginViewState>(LoginViewState.Password);
    const [totpSecret, setTotpSecret] = useState<{
        base64: string;
        qrCode: string;
    }>();
    const [email, setEmail] = useState<string>("");
    useAuthErrorPopup();
    const searchParams = useSearchParams();
    const redirectUrl = searchParams.get("redirectURL");

    const loginWithoutOtp: FormSubmissionHandler<LoginFormValues> = async (
        values,
        { setStatus, setSubmitting },
    ) => {
        setSubmitting(true);
        try {
            const res = await authApi.login(values);
            if (res.mfaRequired) {
                if (res.mfaSecret) {
                    setState(LoginViewState.SetupOtp);
                    setTotpSecret({
                        base64: res.mfaSecret.base64,
                        qrCode: `${PNG_HEADER}${res.mfaSecret.qrcodePngBase64}`,
                    });
                } else {
                    setEmail(values.username);
                    setState(LoginViewState.ValidateOtp);
                }
            } else {
                dispatch(setLoggedIn());
                dispatch(setCanRedirect(true));
                if (redirectUrl) {
                    window.location.replace(redirectUrl);
                }
            }
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers((next) => (e) => {
                if (isApiError(e) && e.status === 403) {
                    setStatus(
                        "The Email and/or Password you entered is incorrect. Please try again.",
                    );
                } else {
                    next(e);
                }
            })(e as GenericError);
        }
    };

    const confirmOtp: FormSubmissionHandler<{ otp: string }> = async (
        values,
        { setErrors, setSubmitting },
    ) => {
        setSubmitting(true);
        try {
            await twoFaApi.confirm(Number.parseFloat(values.otp));
            dispatch(setLoggedIn());
            dispatch(setCanRedirect(true));
            if (redirectUrl) {
                window.location.replace(redirectUrl);
            }
            history.push("/");
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers((next) => (e) => {
                if (isApiError(e)) {
                    setErrors({
                        otp: "You didn't enter the expected verification code. Please try again.",
                    });
                } else {
                    next(e);
                }
            })(e as GenericError);
        }
    };

    switch (state) {
        case LoginViewState.Password:
            return <LoginForm onSubmit={loginWithoutOtp} />;
        case LoginViewState.ValidateOtp:
            return (
                <ValidateOtpForm
                    onSubmit={confirmOtp}
                    onBack={() => setState(LoginViewState.Password)}
                    forgotOTP={() => setState(LoginViewState.ForgotOtp)}
                />
            );
        case LoginViewState.SetupOtp:
            return (
                <SetupOtpForm
                    secret={totpSecret?.base64 ?? ""}
                    qrCode={totpSecret?.qrCode ?? ""}
                    onSubmit={confirmOtp}
                    onBack={() => setState(LoginViewState.Password)}
                />
            );
        case LoginViewState.ForgotOtp:
            return (
                <ForgotOTPForm
                    email={email}
                />
            );
    }
};

export default LoginView;
